import type { CallableCancellableAction } from '../../ros/types';

import type { CacheBackend } from './ReadThroughCache';

/**
 * Read through cache that will return the cached value if available, otherwise it will
 * call the `getValue` function to get the value and cache it.
 *
 * @param Key The type of the key used to get the value.
 * @param Value The type of the value to be cached.
 */
export class ReadThroughCacheAsync<Key, Value> {
  public getCount: number = 0;

  public missCount: number = 0;

  protected cacheBackend: CacheBackend<Value>;

  private readonly getValue: (key: Key) => CallableCancellableAction<Value>;

  private readonly getCacheKey: (key: Key) => any;

  /**
   * @param getValue Function to fetch the value if the key is not in the cache
   * @param getCacheKey Function to transform the key to a cache key
   * @param cache
   */
  public constructor({
    getValue,
    getCacheKey = (key) => key,
    cacheBackend,
  }: {
    getValue: (key: Key) => CallableCancellableAction<Value>;
    getCacheKey?: (key: Key) => any;
    cacheBackend: CacheBackend<Value>;
  }) {
    this.cacheBackend = cacheBackend;
    this.getValue = getValue;
    this.getCacheKey = getCacheKey;
  }

  public async getSkipCache(key: Key): Promise<Value> {
    const action = this.getValue(key);
    const value = await action.call();

    return value;
  }

  public async get(key: Key): Promise<Value> {
    const cacheKey = this.getCacheKey(key);

    this.getCount += 1;

    const cachedValue = this.cacheBackend.get(cacheKey);

    if (cachedValue === undefined) {
      this.missCount += 1;
      const value = await this.getSkipCache(key);
      this.cacheBackend.set(cacheKey, value);

      return value;
    }

    return cachedValue;
  }

  public getCancellableSkipCache(key: Key): CallableCancellableAction<Value> {
    const action = this.getValue(key);

    return {
      call: () => action.call(),
      cancel: () => action.cancel(),
    };
  }

  public getCancellable(key: Key): CallableCancellableAction<Value> {
    const cacheKey = this.getCacheKey(key);

    this.getCount += 1;

    const cachedValue = this.cacheBackend.get(cacheKey);

    if (cachedValue === undefined) {
      this.missCount += 1;

      const cancellableAction = this.getCancellableSkipCache(key);

      // Create a new cancellable action that wraps the original one
      return {
        call: async () => {
          const value = await cancellableAction.call();
          this.cacheBackend.set(cacheKey, value);

          return value;
        },
        cancel: cancellableAction.cancel,
      };
    }

    // For cached values, return a simple action that resolves immediately
    return {
      call: () => Promise.resolve(cachedValue),
      cancel: () => Promise.resolve(), // No-op for cached values
    };
  }

  public set(key: Key, value: Value) {
    this.cacheBackend.set(this.getCacheKey(key), value);
  }

  public has(key: Key) {
    return this.cacheBackend.has(this.getCacheKey(key));
  }

  public reset(): void {
    this.cacheBackend.reset();
    this.getCount = 0;
    this.missCount = 0;
  }

  public get hitCount(): number {
    return this.getCount - this.missCount;
  }

  public size(): number {
    return this.cacheBackend.size();
  }
}
